import { Calendar, Modal } from "@ews-admin/global-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Appointment } from "../../types/appointment.definition";
import { Patient } from "../../types/patient.definition";

interface PatientAppointmentsModalProps {
  patient: Patient;
  onClose: () => void;
}

export const PatientAppointmentsModal = ({
  patient,
  onClose,
}: PatientAppointmentsModalProps) => {
  const { t } = useTranslation();
  const [appointments] = useState<Appointment[]>(
    patient.upcomingAppointments || []
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return "bg-green-100 text-green-800 border-green-200";
      case "CANCELED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("profile.patient.appointmentsModal.title")}
      variant="info"
      size="4xl"
      primaryAction={t("profile.patient.appointmentsModal.close")}
      onPrimaryAction={onClose}
    >
      <p className="mb-6 -mt-2 text-sm text-neutral-600">
        {patient.firstName} {patient.lastName}
      </p>

      <div className="space-y-4">
            {appointments.length > 0 ? (
              appointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="p-4 rounded-lg border border-neutral-200"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                          appointment.status
                        )}`}
                      >
                        {appointment.status}
                      </span>
                      <span className="text-sm text-neutral-600">
                        {new Date(
                          appointment.appointmentTime
                        ).toLocaleDateString(undefined, { timeZone: "Africa/Dakar" })}{" "}
                        {t("profile.patient.appointmentsModal.at")}{" "}
                        {new Date(appointment.appointmentTime).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Dakar" })}
                      </span>
                    </div>
                    <span className="text-sm text-neutral-600">
                      {t("profile.patient.appointmentsModal.doctorPrefix")}{" "}
                      {appointment.doctor?.firstName}{" "}
                      {appointment.doctor?.lastName}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-neutral-700">
                        {t("profile.patient.appointmentsModal.type")}:
                      </span>
                      <span className="ml-2 text-neutral-600">
                        {appointment.appointmentType}
                      </span>
                    </div>
                    {appointment.reason && (
                      <div>
                        <span className="font-medium text-neutral-700">
                          {t("profile.patient.appointmentsModal.reason")}:
                        </span>
                        <span className="ml-2 text-neutral-600">
                          {appointment.reason}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-neutral-500">
                <Calendar className="mx-auto mb-4 w-12 h-12 text-neutral-300" />
                <p>{t("profile.patient.appointmentsModal.noAppointments")}</p>
              </div>
            )}
      </div>
    </Modal>
  );
};
